import React, { useState } from 'react';
import { AuthService } from '../service/auth.service';
import { Layout, Card, Form, Input, Button, Typography, Alert } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { useTheme } from '../components/ThemeProvider';

const { Content } = Layout;
const { Title, Text } = Typography;

const ForgotPasswordPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { isDark } = useTheme();

  const onFinish = async (values: { email: string }) => {
    try {
      setLoading(true);
      setError('');

      // Call the forgot password API
      await AuthService.forgotPassword(values.email);

      setSuccess(true);
      form.resetFields();
    } catch (err: any) {
      setError(err.message || 'Failed to process your request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: isDark ? '#141414' : '#f0f2f5' }}>
      <Content style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '24px'
      }}>
        <Card
          className="login-form-card"
          variant="borderless"
          style={{
            width: '100%',
            maxWidth: '450px',
            borderRadius: '8px',
            backgroundColor: isDark ? '#1f1f1f' : '#fff',
          }}
          styles={{ body: { padding: '32px' } }}
        >
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <Title level={3}>Forgot Password</Title>
            <Text type="secondary">
              Enter your email address and we'll send you a link to reset your password
            </Text>
          </div>

          {error && <Alert style={{ marginBottom: '16px' }} message={error} type="error" showIcon />}

          {success ? (
            <Alert
              message="Reset Link Sent"
              description="If an account exists with this email, you will receive a password reset link shortly. Please check your email."
              type="success"
              showIcon
              style={{ marginBottom: '16px' }}
            />
          ) : (
            <Form
              form={form}
              onFinish={onFinish}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Please enter a valid email!' }
                ]}
              >
                <Input
                  prefix={<MailOutlined />}
                  placeholder="Email address"
                />
              </Form.Item>

              <Form.Item style={{ marginTop: '24px' }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  loading={loading}
                >
                  Send Reset Link
                </Button>
              </Form.Item>
            </Form>
          )}

          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <Button type="link" onClick={() => window.location.href = '/'}>
              Back to Home
            </Button>
          </div>
        </Card>
      </Content>
    </Layout>
  );
};

export default ForgotPasswordPage;
